import type { ForwardedRef } from 'react';
import React from 'react';
import type { CSSObject } from 'styled-components';
import styled from 'styled-components';
import type {
  LayoutProps,
  PositionProps,
  TextColorProps,
  FlexboxProps,
  TypographyProps,
  SpaceProps,
  OpacityProps,
} from 'styled-system';
import {
  color,
  flexbox,
  layout,
  position,
  space,
  typography,
} from 'styled-system';
import TextAncestorContext from './TextAncestorContext';
import type { Theme } from '../../theme';

export interface TextProps
  extends TextColorProps,
    FlexboxProps,
    LayoutProps,
    OpacityProps,
    PositionProps,
    SpaceProps,
    TypographyProps {
  isUppercase?: boolean;
  isUnselectable?: boolean;
  isSingleLine?: boolean;
  variant?: keyof Theme['button'];
}

/**
 * Copied from react-native-web View styles for compatibility
 * https://github.com/necolas/react-native-web/blob/67a0bde7b3c15fd2b47a42978d7f9f3ffcda3ff6/packages/react-native-web/src/exports/View/index.js#L146
 */
const textStyles: CSSObject = {
  backgroundColor: 'transparent',
  border: '0 solid black',
  boxSizing: 'border-box',
  display: 'inline',
  listStyle: 'none',
  lineHeight: 'normal',
  margin: 0,
  padding: 0,
  position: 'relative',
  textAlign: 'start',
  textDecoration: 'none',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
};

const SpanInner = (
  props: React.JSX.IntrinsicAttributes & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: string | React.ComponentType<any>;
    'data-testid'?: string;
  } & React.Attributes &
    Pick<
      React.HTMLAttributes<HTMLSpanElement>,
      'className' | 'style' | 'children'
    > &
    TextProps,
  ref?: ForwardedRef<HTMLSpanElement>,
) => {
  const { as, children, className, style } = props;
  const As = as ?? 'span';
  return (
    <As
      ref={ref}
      style={style}
      className={className}
      data-testid={props['data-testid']}
    >
      {children}
    </As>
  );
};
const Span = React.forwardRef(SpanInner);

const DivInner = (
  props: React.JSX.IntrinsicAttributes & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: string | React.ComponentType<any>;
    'data-testid'?: string;
  } & React.Attributes &
    Pick<
      React.HTMLAttributes<HTMLDivElement>,
      'className' | 'style' | 'children'
    > &
    TextProps,
  ref?: ForwardedRef<HTMLDivElement>,
) => {
  const { as, children, className, style } = props;
  const As = as ?? 'div';
  return (
    <As
      ref={ref}
      style={style}
      className={className}
      data-testid={props['data-testid']}
    >
      {children}
    </As>
  );
};
const Div = React.forwardRef(DivInner);

const TextComponent = styled.div<TextProps>(
  textStyles,
  color,
  flexbox,
  layout,
  position,
  space,
  typography,
  (props): CSSObject => {
    return {
      fontFamily: `'Inter', 'Space Grotesk', 'Helvetica', fallback-font, sans-serif`,
      textTransform: props.isUppercase ? 'uppercase' : 'none',
      userSelect: props.isUnselectable ? 'none' : 'text',
      whiteSpace: props.isSingleLine ? 'nowrap' : 'initial',
    };
  },
);

const TextInner = (
  props: React.ComponentProps<typeof Div>,
  ref?: ForwardedRef<HTMLDivElement | HTMLSpanElement>,
) => {
  const hasTextAncestor = React.useContext(TextAncestorContext);

  return hasTextAncestor ? (
    <TextComponent as={Span} ref={ref} {...props} />
  ) : (
    <TextAncestorContext.Provider value={true}>
      <TextComponent as={Div} ref={ref} {...props} />
    </TextAncestorContext.Provider>
  );
};
const Text = React.forwardRef(TextInner);

Text.defaultProps = { fontSize: 2 };

export default Text;
